import { Button, MenuItem, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useKeyValues } from 'api/keyValues';
import {
  type ExposureOrGroup,
  type ObjectLiteral,
  useSubmission,
  useUnderwritingQuestions,
} from 'api/submission';
import {
  fetchVintelligenceMakes,
  fetchVintelligenceModels,
  type IVintelligenceMakesElement,
} from 'api/vintelligence';
import {
  addVehicleProhibitedErrorMessage,
  APPLICATION_STEPS,
  LICENSE_PLATE_STATE,
  SHORT_VIN_INFO_MESSAGE,
  VALIDATION_MESSAGES,
  VIN_LENGTH,
} from 'common/constants';
import { LAUNCH_DARKLY_FLAGS } from 'common/launchDarklyFlags';
import { MissingVehicleVinModal } from 'components/MissingVehicleVinModal';
import { Modal } from 'components/Modal';
import { FormControl } from 'components/lib/FormControl';
import { NumberFormatWithWrapper } from 'components/lib/NumberFormat';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import {
  type Choice,
  type ExtendedQuestionEngineState,
  FieldQuestionText,
  handleShowCondition,
  QuestionEngine,
  type QuestionEngineProps,
  useQuestionEngine,
} from 'components/lib/QuestionEngine';
import { Select } from 'components/lib/Select';
import { SkeletonList } from 'components/lib/SkeletonList';
import { TextField } from 'components/lib/TextField';
import type { IToggleButton } from 'components/lib/ToggleButton';
import { ToggleButtonsGroup } from 'components/lib/ToggleButtonsGroup';
import { useFormik } from 'formik';
import { formatThreeConfigVersion } from 'helpers/utils';
import { isShortVin, sanitizeVinInput, shouldApplyShortVinRules } from 'helpers/vehicleVin';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useScrollToFirstErroredInput } from 'hooks/useScrollToFirstErroredInput';
import { isEmpty, omit, uniqBy } from 'lodash-es';
import {
  type ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useFullscreenLoader } from 'store/features/UI';

import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { displayToastMessage } from 'components/ToastMessage';
import * as yup from 'yup';
import type {
  ITblDepreciation,
  ITblGCW,
  IVehicle,
  IVehicleBodyCategoryTable,
  IVehicleBodyStyleToCategoryTable,
  IVehicleGCWTable,
} from '../../Vehicles.types';
import {
  checkIsVehicleProhibitedToAdd,
  getVehicleBodyCategoryIconProps,
  getVehicleSubtype,
  getVehicleType,
  shouldShowVehicleStatedAmount,
  validateVINFormat,
  verifyVIN,
} from './VehicleModal.helper';
import { VerifyVINButtonStyle, VerifyVINButtonTextStyle } from './VehicleModal.styles';
import type {
  IFieldMap,
  IVerifyVINStatus,
  VehicleModalProps,
  VehicleModalRef,
} from './VehicleModal.types';

type TExposure = ExposureOrGroup<IVehicle>;

const { fields: vehicleFields } = APPLICATION_STEPS.vehicles;
const fieldMap = vehicleFields.reduce((acc, field) => {
  acc[field.code] = field;
  return acc;
}, {} as IFieldMap);

const vehicleUwQuestionCode = 'vehicle_details';

const VehicleModal = forwardRef<VehicleModalRef | undefined, VehicleModalProps>(
  // eslint-disable-next-line complexity
  ({ handleAdd, handleUpdate }, ref) => {
    const { id } = useParams() as { id: string };
    const { data: submission } = useSubmission(id);
    const themeHook = useTheme();
    const isMobileLayout = useMediaQuery(themeHook.breakpoints.down('sm'));
    const [editingExposure, setEditingExposure] = useState<TExposure>({} as TExposure);
    const [vehicle, setVehicleData] = useState<IVehicle>({} as IVehicle);
    const [open, setOpen] = useState(false);
    const [fields, setFields] = useState<IFieldMap>(fieldMap);
    const [verifyVINStatus, setVerifyVINStatus] = useState<IVerifyVINStatus>('blank');
    const [showMakeModelSkeletonTogether, setShowMakeModelSkeletonTogether] = useState(false);
    const [isMissingVinModalOpen, setIsMissingVinModalOpen] = useState(false);
    const [isVehicleVinRequiredEnabled] = useFeatureFlags([
      {
        flagKey: LAUNCH_DARKLY_FLAGS['vehicle-vin-required-enabled'],
        defaultValue: false,
      },
    ]);
    const { setFullscreenLoading } = useFullscreenLoader();
    const skeletonHeight = isMobileLayout ? 53.13 : 56;
    const vehMakeCode = useRef<IVintelligenceMakesElement['code'] | ''>('');
    const vehicleYearAdded = submission?.effective_date?.split('-')?.shift() || '';

    const showVehicleStatedAmount = useMemo(
      () =>
        shouldShowVehicleStatedAmount({
          vehBodyCategory: vehicle.veh_body_category,
          vehMSRP: vehicle.veh_msrp,
          vehYear: vehicle.veh_year,
          yearAdded: vehicleYearAdded,
        }),
      [vehicle.veh_body_category, vehicle.veh_msrp, vehicle.veh_year, vehicleYearAdded],
    );

    const { data: questions } = useUnderwritingQuestions(id, true);

    const vehicleQuestionFields = useMemo(() => {
      const vehicleUWQuestionSection = questions?.allQuestions.filter(
        (q) => q.code === vehicleUwQuestionCode,
      );

      return vehicleUWQuestionSection?.at(0)?.fields?.at(0)?.nested_fields || [];
    }, [vehicleUwQuestionCode]);

    const { validateFormik: validataVehicleUWQuestionFormik, ...questionEngineProps } =
      useQuestionEngine<ObjectLiteral>({ fields: vehicleQuestionFields });
    const {
      formik: vehicleUWFormik,
      state: vehicleUWState,
      setState: setVehicleUWQuestionState,
    } = questionEngineProps;

    const isEditing = !isEmpty(editingExposure);
    const title = `${isEditing ? 'Update' : 'Add'} vehicle`;
    const quoteState = submission?.us_state;

    const { data: vehicleBodyCategory, isLoading: vehicleBodyCategoriesLoading } = useKeyValues<
      IVehicleBodyCategoryTable[]
    >({
      key: 'vehicleBodyCategory',
    });

    const { data: bodyStyleToCategoryMapping } = useKeyValues<IVehicleBodyStyleToCategoryTable[]>({
      key: 'vehicleBodyStyleToCategory',
    });

    const { data: gcwMapping } = useKeyValues<IVehicleGCWTable[]>({
      key: 'gcwMapping',
    });

    const { data: tblGCW } = useKeyValues<ITblGCW[]>({
      key: 'tblGCW',
    });

    const { data: tblDepreciation } = useKeyValues<ITblDepreciation[]>({
      key: 'tblDepreciation',
    });

    const vehicleBodyCategoryByState = useMemo(
      () => vehicleBodyCategory?.value?.filter((category) => category.state === quoteState),
      [vehicleBodyCategory, quoteState],
    );

    const uniqueVehicleBodyCategory = useMemo(
      () => uniqBy(vehicleBodyCategoryByState, 'veh_body_category'),
      [vehicleBodyCategoryByState],
    );

    const uniqueVehicleBodySubCategory = useMemo(
      () =>
        uniqBy(
          vehicleBodyCategoryByState?.filter(
            (v) => v.veh_body_category === vehicle.veh_body_category,
          ),
          'veh_body_subcategory',
        ),
      [vehicleBodyCategoryByState, vehicle.veh_body_category],
    );

    const vehicleBodyCategoryChoices: IToggleButton[] = useMemo(
      () =>
        uniqueVehicleBodyCategory.map((category) => ({
          value: category.veh_body_category,
          label: category.veh_body_category,
          ...getVehicleBodyCategoryIconProps(category.veh_body_category),
        })),
      [uniqueVehicleBodyCategory],
    );

    const vehicleBodySubCategoryChoices: Choice[] = useMemo(
      () =>
        uniqueVehicleBodySubCategory.map((category) => ({
          code: category.veh_body_subcategory,
          name: category.veh_body_subcategory,
        })),
      [uniqueVehicleBodySubCategory],
    );

    const vehicleMakeChoices = uniqBy(fields?.veh_make?.choices, 'name');
    const vehicleModelChoices = uniqBy(fields?.veh_model?.choices, 'name');

    const handleVehicleMakes = async (year: IVehicle['veh_year']) => {
      try {
        fields.veh_make.additional_data = {
          ...fields.veh_make.additional_data,
          loading: true,
        };
        const makes = await fetchVintelligenceMakes({ year, consumer_submission_id: id });

        if (makes) {
          fields.veh_make.choices = makes.map((make) => ({
            code: make.code,
            name: make.description,
          }));
        }
      } catch (_error) {
        displayBackendErrorMessage(_error);
      } finally {
        fields.veh_make.additional_data = {
          ...fields.veh_make.additional_data,
          loading: false,
        };
        setFields({ ...fields });
      }
    };

    const handleVehicleModels = async (year: IVehicle['veh_year'], make: IVehicle['veh_make']) => {
      try {
        fields.veh_model.additional_data = {
          ...fields.veh_model.additional_data,
          loading: true,
        };

        const vehicleModels = await fetchVintelligenceModels({
          year,
          make,
          consumer_submission_id: id,
        });

        if (vehicleModels) {
          fields.veh_model.choices = vehicleModels.map((model) => ({
            code: model.code,
            name: model.description,
          }));
        }
      } catch (_error) {
        displayBackendErrorMessage(_error);
      } finally {
        fields.veh_model.additional_data = {
          ...fields.veh_model.additional_data,
          loading: false,
        };
        setFields({ ...fields });
      }
    };

    // eslint-disable-next-line complexity
    const setState = async <K extends keyof IVehicle>(key: K, value: IVehicle[K]) => {
      formik.setFieldValue(key as string, value);
      setVehicleData((prevState) => ({ ...prevState, [key]: value }));
      if (showMakeModelSkeletonTogether) {
        // Need to show veh_make and veh_model skeleton once the model is opened.
        // Then, make it false otherwise, once the veh_make is updated
        // veh_make field will have skeleton which is not expected.
        setShowMakeModelSkeletonTogether(false);
      }

      if (key === 'is_vin_number_available') {
        if (value === 'No') {
          setVehicleData((prevState) => ({
            ...prevState,
            veh_vin: '',
            veh_vin_isvalid: 'No',
            veh_msrp: '',
            veh_msrp_dpr: '',
            veh_stated_amt: '',
          }));
          formik.setValues({
            ...formik.values,
            is_vin_number_available: value as string,
            veh_vin: '',
            veh_vin_isvalid: 'No',
            veh_msrp: '',
            veh_msrp_dpr: '',
            veh_stated_amt: '',
          });
          setVerifyVINStatus('blank');
        }
      }

      if (key === 'veh_body_category') {
        setVehicleData((prevState) => ({
          ...prevState,
          veh_body_subcategory: '',
          veh_type: '',
          veh_gvw: '',
          veh_subtype: '',
        }));

        formik.setValues({
          ...formik.values,
          veh_body_category: value as string,
          veh_body_subcategory: '',
          veh_gvw: '',
        });
      }

      if (key === 'veh_body_subcategory') {
        const veh_type = getVehicleType({
          bodyCategory: vehicle.veh_body_category,
          bodySubCategory: value as string,
          policyData: submission?.data?.policy_data,
          table: vehicleBodyCategory?.value,
          vehicleLocationState: submission?.us_state,
          locFilingSetId: submission?.data?.exposures?.location?.at(0)?.data?.loc_filing_set_id,
        });

        const veh_subtype = getVehicleSubtype({
          veh_body_category: vehicle.veh_body_category,
          veh_body_subcategory: value as string,
          veh_type,
          veh_gvw: vehicle?.veh_gvw,
          gcwMapping: gcwMapping?.value,
          tblGCW: tblGCW?.value,
        });

        setVehicleData((prevState) => ({
          ...prevState,
          veh_type,
          veh_subtype,
        }));
      }

      if (key === 'veh_gvw') {
        const veh_subtype = getVehicleSubtype({
          veh_body_category: vehicle.veh_body_category,
          veh_body_subcategory: vehicle?.veh_body_subcategory,
          veh_type: vehicle?.veh_type,
          veh_gvw: value,
          gcwMapping: gcwMapping?.value,
          tblGCW: tblGCW?.value,
        });

        setVehicleData((prevState) => ({
          ...prevState,
          veh_subtype,
        }));
      }

      if (key === 'veh_year') {
        await handleVehicleMakes(value as string);
        setVehicleData((prevState) => ({ ...prevState, veh_make: '', veh_model: '' }));
        formik.setValues({
          ...formik.values,
          veh_year: value as string,
          veh_model: '',
          veh_make: '',
        });
      }

      if (key === 'veh_make' && !isEmpty(value) && vehMakeCode.current) {
        await handleVehicleModels(vehicle.veh_year, vehMakeCode.current as string);
        setVehicleData((prevState) => ({ ...prevState, veh_model: '' }));
        formik.setFieldValue('veh_model', '');
      }

      if (key === 'veh_vin') {
        setVerifyVINStatus('blank');
      }
    };

    // Short VINs are allowed for everything but trailers, which keep the exactly-17 rule (ENG-12264).
    const applyShortVinRules = shouldApplyShortVinRules(isVehicleVinRequiredEnabled, vehicle);

    const formik = useFormik<IVehicle>({
      initialValues: {
        ...editingExposure.data,
      },
      validationSchema: yup.object({
        [fields.is_vin_number_available.code]: yup.string().required(),
        [fields.veh_vin.code]: yup.string().when('is_vin_number_available', {
          is: (is_vin_number_available: string) => is_vin_number_available === 'Yes',
          then: () =>
            applyShortVinRules
              ? yup.string().required()
              : yup
                  .string()
                  .required()
                  .test(
                    'vin-format',
                    VALIDATION_MESSAGES.invalidVINFormat,
                    (val) => !val || validateVINFormat(val),
                  ),
          otherwise: () => yup.string().notRequired(),
        }),
        [fields.veh_body_category.code]: yup.string().required(),
        [fields.veh_body_subcategory.code]: yup.string().required(),
        [fields.veh_year.code]: yup.string().required(),
        [fields.veh_make.code]: yup.string().required(),
        [fields.veh_model.code]: yup.string().required(),
        [fields.veh_trim.code]: yup.string().notRequired(),
        [fields.veh_gvw.code]: yup.string().required(),
        [fields.veh_radius.code]: yup.string().required(),
        ...(showVehicleStatedAmount && {
          [fields.veh_stated_amt.code]: yup
            .string()
            .required()
            .test('greater-than-one', 'Invalid value', (val) => {
              const num = Number(String(val).trim());
              return num >= 1;
            }),
        }),
      }),
      onSubmit: () => {},
    });

    useScrollToFirstErroredInput({ formik });

    const onVerifyVINClick = async () => {
      try {
        setVerifyVINStatus('loading');
        setFullscreenLoading(true, 'Validating the VIN.');
        const { vehMakeChoices, vehModelChoices, updatedFields, status } = await verifyVIN({
          veh_vin: vehicle.veh_vin,
          bodyStyleToCategoryMapping: bodyStyleToCategoryMapping?.value!,
          gcwMapping: gcwMapping?.value!,
          tblGCW: tblGCW?.value!,
          tblDepreciation: tblDepreciation?.value!,
          yearAdded: vehicleYearAdded,
          consumer_submission_id: id,
        });
        if (status === 'success') {
          fields.veh_make.choices = vehMakeChoices;
          fields.veh_model.choices = vehModelChoices;
          setFields({ ...fields });
          setVehicleData((prevState) => ({ ...prevState, ...updatedFields }));
          formik.setValues({ ...formik.values, ...omit(updatedFields, 'veh_subtype', 'veh_type') });
        } else {
          displayToastMessage('ERROR', 'Invalid VIN. Retry or enter vehicle details manually.');
        }
        setVerifyVINStatus(status);
      } catch (_error) {
        displayBackendErrorMessage(_error);
      } finally {
        setFullscreenLoading(false);
      }
    };

    const fieldHasError = (code: string) =>
      formik.touched[`${code}`] && Boolean(formik.errors[`${code}`]);
    const fieldErrorMessage = (code: string) =>
      fieldHasError(code) && (formik.errors[`${code}`] as string);

    const shortVinInfoMessage =
      applyShortVinRules && isShortVin(vehicle?.veh_vin) ? SHORT_VIN_INFO_MESSAGE : '';

    useImperativeHandle(ref, () => ({
      openModal: (exposure) => {
        if (exposure) {
          setEditingExposure(exposure);
          setVehicleData(exposure.data);
          formik?.setValues(exposure.data);
          vehicleUWFormik.setValues(exposure.data.underwriting_question || {});
          setVehicleUWQuestionState?.(exposure?.data?.underwriting_question || {});
          if (!isEmpty(exposure.data?.veh_year)) {
            setShowMakeModelSkeletonTogether(true);
            handleVehicleMakes(exposure.data.veh_year).then(() => {
              if (!isEmpty(exposure.data?.veh_make)) {
                const exposureMakeCode =
                  fields.veh_make.choices?.find((make) => make.name === exposure.data.veh_make)
                    ?.code || '';

                handleVehicleModels(exposure.data.veh_year, exposureMakeCode);
              }
            });
          }
          if (exposure.data?.veh_vin_isvalid === 'Yes') {
            setVerifyVINStatus('success');
          }
        } else {
          const isLicensePlateStateQuestionPresent = vehicleQuestionFields.find(
            (field) => field.code === LICENSE_PLATE_STATE,
          );

          if (
            isLicensePlateStateQuestionPresent &&
            isEmpty(vehicleUWFormik.values[LICENSE_PLATE_STATE])
          ) {
            const submissionState = submission?.us_state || '';
            vehicleUWFormik.setFieldValue(LICENSE_PLATE_STATE, submissionState);
            setVehicleUWQuestionState?.({
              licensePlateState: submissionState,
            });
          }
        }

        setOpen(true);
      },
    }));

    const resetDrawer = () => {
      setOpen(false);
      formik?.resetForm();
      vehicleUWFormik?.resetForm();
      setVehicleData({} as IVehicle);
      setVehicleUWQuestionState?.({});
      setVerifyVINStatus('blank');
      setEditingExposure({} as TExposure);
      setShowMakeModelSkeletonTogether(false);
      // Clear veh_make and veh_model choices
      fields.veh_make.choices = [];
      fields.veh_model.choices = [];
      setFields({ ...fields });
    };

    const validateFormik = async () => {
      await formik.submitForm();
      const errors = await formik.validateForm();
      if (!isEmpty(errors)) {
        await formik.setTouched(
          {
            ...formik.touched,
            ...Object.keys(fields).reduce((acc, key) => {
              acc[key] = true;
              return acc;
            }, {}),
          },
          false,
        );

        return false;
      }

      return true;
    };

    const persistVehicle = async () => {
      const vehiclePayload: IVehicle = {
        ...formik.values,
        underwriting_question: vehicleUWState,
      };

      if (isEditing) {
        await handleUpdate(vehiclePayload, editingExposure?.id);
      } else {
        await handleAdd(vehiclePayload);
      }

      resetDrawer();
    };

    const handleAction = async () => {
      const isValid = await validateFormik();
      const isUWQuestionsValid = await validataVehicleUWQuestionFormik();

      if (!isValid || !isUWQuestionsValid) {
        return false;
      }

      const isVehicleProhibitedToAdd = checkIsVehicleProhibitedToAdd({
        policy_data: submission?.data?.policy_data || {},
        veh_type: vehicle.veh_type,
      });

      if (isVehicleProhibitedToAdd) {
        displayToastMessage('ERROR', addVehicleProhibitedErrorMessage);
        return false;
      }

      // Warns on every blank-VIN save, not only the first; accepting saves the vehicle as-is.
      if (applyShortVinRules && isEmpty(formik.values.veh_vin)) {
        setIsMissingVinModalOpen(true);
        return false;
      }

      await persistVehicle();
      return true;
    };

    const handleMissingVinAccept = async () => {
      setIsMissingVinModalOpen(false);
      await persistVehicle();
    };

    const handleSetVIN = (e: ChangeEvent<HTMLInputElement>) => {
      setState('veh_vin', sanitizeVinInput(e.target.value));
    };

    return (
      <>
        <Modal
          open={open}
          title={title}
          onClose={resetDrawer}
          onCloseIconClick={() => resetDrawer()}
          modalContent={
            vehicleBodyCategoriesLoading ? (
              <Stack gap={1.5}>
                <SkeletonList count={7} height={skeletonHeight} />
              </Stack>
            ) : (
              <Stack gap={4}>
                <Stack gap={1.5}>
                  <FieldQuestionText field={fields.is_vin_number_available} />
                  <FormControl
                    error={fieldHasError(fields.is_vin_number_available.code)}
                    errorMessage={fieldErrorMessage(fields.is_vin_number_available.code)}
                  >
                    <ToggleButtonsGroup
                      id={fields.is_vin_number_available.code}
                      value={vehicle?.is_vin_number_available}
                      onChange={(_, value: string) => {
                        setState('is_vin_number_available', value);
                      }}
                      options={
                        fields.is_vin_number_available.choices?.map((choice) => ({
                          value: choice.code,
                          label: choice.name,
                        })) || []
                      }
                    />
                  </FormControl>

                  {handleShowCondition(
                    fields.veh_vin,
                    vehicle as unknown as ExtendedQuestionEngineState,
                  ) && (
                    <>
                      <TextField
                        id={fields.veh_vin.code}
                        field={fields.veh_vin}
                        value={vehicle?.veh_vin}
                        error={fieldHasError(fields.veh_vin.code)}
                        helperText={fieldErrorMessage(fields.veh_vin.code) || shortVinInfoMessage}
                        onChange={handleSetVIN}
                        inputProps={{ maxLength: VIN_LENGTH }}
                      />
                      <Button
                        onClick={onVerifyVINClick}
                        variant="text"
                        disabled={
                          isEmpty(vehicle?.veh_vin) ||
                          verifyVINStatus === 'loading' ||
                          verifyVINStatus === 'success'
                        }
                        disableRipple
                        sx={VerifyVINButtonStyle}
                      >
                        <Typography sx={VerifyVINButtonTextStyle}>
                          {verifyVINStatus === 'success' ? 'VIN Verified' : 'Verify VIN'}
                        </Typography>
                      </Button>
                    </>
                  )}
                </Stack>

                <Stack gap={1.5}>
                  <FieldQuestionText field={fields.veh_body_category} />
                  <FormControl
                    error={fieldHasError(fields.veh_body_category.code)}
                    errorMessage={fieldErrorMessage(fields.veh_body_category.code)}
                  >
                    <ToggleButtonsGroup
                      id={fields.veh_body_category.code}
                      value={vehicle?.veh_body_category}
                      onChange={(_, value: string) => {
                        setState('veh_body_category', value);
                      }}
                      options={vehicleBodyCategoryChoices}
                      isReadOnly={verifyVINStatus === 'success'}
                    />
                  </FormControl>
                </Stack>

                <Stack gap={1.5}>
                  <Typography variant="h2">Tell us more about your vehicle</Typography>
                  <FormControl
                    field={fields.veh_body_subcategory}
                    error={fieldHasError(fields.veh_body_subcategory.code)}
                    errorMessage={fieldErrorMessage(fields.veh_body_subcategory.code)}
                    renderLabel
                  >
                    <Select
                      id={fields.veh_body_subcategory.code}
                      value={vehicle?.veh_body_subcategory}
                      label={fields.veh_body_subcategory.name}
                      labelId={`${fields.veh_body_subcategory.code}_label`}
                      error={fieldHasError(fields.veh_body_subcategory.code)}
                      onChange={(event) => {
                        setState('veh_body_subcategory', event.target.value as string);
                      }}
                      inputProps={{
                        readOnly:
                          !isEmpty(vehicle?.veh_body_subcategory) && verifyVINStatus === 'success',
                      }}
                      isFieldOptional={fields.veh_body_subcategory?.is_optional}
                      optionsCount={vehicleBodySubCategoryChoices.length}
                      preSelectFirstOption={() => {
                        const firstChoice = vehicleBodySubCategoryChoices?.at(0);
                        if (firstChoice) {
                          setState('veh_body_subcategory', firstChoice?.code);
                        }
                      }}
                    >
                      {vehicleBodySubCategoryChoices.map((choice) => (
                        <MenuItem key={choice.code} value={choice.code}>
                          {choice.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    field={fields.veh_year}
                    error={fieldHasError(fields.veh_year.code)}
                    errorMessage={fieldErrorMessage(fields.veh_year.code)}
                    renderLabel
                  >
                    <Select
                      id={fields.veh_year.code}
                      value={vehicle?.veh_year}
                      label={fields.veh_year.name}
                      labelId={`${fields.veh_year.code}_label`}
                      error={fieldHasError(fields.veh_year.code)}
                      onChange={(event) => {
                        setState('veh_year', event.target.value as string);
                      }}
                      inputProps={{
                        readOnly: !isEmpty(vehicle?.veh_year) && verifyVINStatus === 'success',
                      }}
                      isFieldOptional={fields.veh_year?.is_optional}
                    >
                      {fields.veh_year?.choices?.map((choice) => (
                        <MenuItem key={choice.code} value={choice.code}>
                          {choice.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {fields.veh_make?.additional_data?.loading ||
                  (showMakeModelSkeletonTogether && fields.veh_model?.additional_data?.loading) ? (
                    <SkeletonList count={1} height={skeletonHeight} />
                  ) : (
                    <FormControl
                      field={fields.veh_make}
                      error={fieldHasError(fields.veh_make.code)}
                      errorMessage={fieldErrorMessage(fields.veh_make.code)}
                      renderLabel
                    >
                      <Select
                        id={fields.veh_make.code}
                        value={vehicle?.veh_make}
                        label={fields.veh_make.name}
                        error={fieldHasError(fields.veh_make.code)}
                        labelId={`${fields.veh_make.code}_label`}
                        inputProps={{
                          readOnly:
                            fields.veh_make.additional_data?.loading ||
                            (!isEmpty(vehicle?.veh_make) && verifyVINStatus === 'success'),
                        }}
                        onChange={(event) => {
                          setState('veh_make', event.target.value as string);
                        }}
                        isFieldOptional={fields.veh_make?.is_optional}
                        optionsCount={vehicleMakeChoices?.length}
                        preSelectFirstOption={() => {
                          const firstChoice = vehicleMakeChoices?.at(0);
                          if (firstChoice) {
                            setState('veh_make', firstChoice?.name);
                          }
                        }}
                      >
                        {vehicleMakeChoices?.map((choice) => (
                          <MenuItem
                            key={`${choice.code}_${choice.name}`}
                            value={choice.name}
                            onClick={() => (vehMakeCode.current = choice.code)}
                          >
                            {choice.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {fields.veh_make?.additional_data?.loading ||
                  fields.veh_model?.additional_data?.loading ? (
                    <SkeletonList count={1} height={skeletonHeight} />
                  ) : (
                    <FormControl
                      field={fields.veh_model}
                      error={fieldHasError(fields.veh_model.code)}
                      errorMessage={fieldErrorMessage(fields.veh_model.code)}
                      renderLabel
                    >
                      <Select
                        id={fields.veh_model.code}
                        value={vehicle?.veh_model}
                        label={fields.veh_model.name}
                        error={fieldHasError(fields.veh_model.code)}
                        labelId={`${fields.veh_model.code}_label`}
                        inputProps={{
                          readOnly:
                            fields.veh_model.additional_data?.loading ||
                            (!isEmpty(vehicle?.veh_model) && verifyVINStatus === 'success'),
                        }}
                        onChange={(event) => {
                          setState('veh_model', event.target.value as string);
                        }}
                        isFieldOptional={fields.veh_model?.is_optional}
                        optionsCount={vehicleModelChoices?.length}
                        preSelectFirstOption={() => {
                          const firstChoice = vehicleModelChoices?.at(0);
                          if (firstChoice) {
                            setState('veh_model', firstChoice?.name);
                          }
                        }}
                      >
                        {vehicleModelChoices.map((choice) => (
                          <MenuItem key={`${choice.code}_${choice.name}`} value={choice.name}>
                            {choice.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <FormControl
                    field={fields.veh_radius}
                    error={fieldHasError(fields.veh_radius.code)}
                    errorMessage={fieldErrorMessage(fields.veh_radius.code)}
                    renderLabel
                  >
                    <Select
                      id={fields.veh_radius.code}
                      value={vehicle?.veh_radius}
                      label={fields.veh_radius.name}
                      error={fieldHasError(fields.veh_radius.code)}
                      labelId={`${fields.veh_radius.code}_label`}
                      inputProps={{
                        readOnly: fields.veh_radius.additional_data?.loading,
                      }}
                      onChange={(event) => {
                        setState('veh_radius', event.target.value as string);
                      }}
                      isFieldOptional={fields?.veh_radius?.is_optional}
                    >
                      {fields.veh_radius?.choices?.map((choice) => (
                        <MenuItem key={choice.code} value={choice.code}>
                          {choice.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    field={fields.veh_gvw}
                    error={fieldHasError(fields.veh_gvw.code)}
                    errorMessage={fieldErrorMessage(fields.veh_gvw.code)}
                    renderLabel
                  >
                    <Select
                      id={fields.veh_gvw.code}
                      value={vehicle?.veh_gvw}
                      error={fieldHasError(fields.veh_gvw.code)}
                      label={fields.veh_gvw.name}
                      labelId={`${fields.veh_gvw.code}_label`}
                      inputProps={{
                        readOnly:
                          isEmpty(vehicle?.veh_body_subcategory) ||
                          fields.veh_gvw.additional_data?.loading ||
                          (!isEmpty(vehicle?.veh_gvw) && verifyVINStatus === 'success'),
                      }}
                      onChange={(event) => {
                        setState('veh_gvw', event.target.value as string);
                      }}
                      isFieldOptional={fields?.veh_gvw?.is_optional}
                    >
                      {fields.veh_gvw?.choices?.map((choice) => (
                        <MenuItem key={choice.code} value={choice.code}>
                          {choice.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    field={fields.veh_trim}
                    value={vehicle.veh_trim}
                    isFieldOptional={fields.veh_trim.is_optional}
                    onChange={(event) => {
                      setState('veh_trim', event.target.value as string);
                    }}
                  />

                  {vehicle?.veh_msrp && (
                    <NumberFormatWithWrapper
                      field={fields.veh_msrp}
                      value={vehicle?.veh_msrp}
                      currencyInput
                      inputProps={{ prefix: '$ ' }}
                      isFieldReadOnly={fields.veh_msrp.is_readonly}
                    />
                  )}

                  {vehicle?.veh_msrp_dpr && (
                    <NumberFormatWithWrapper
                      field={fields.veh_msrp_dpr}
                      value={vehicle?.veh_msrp_dpr}
                      currencyInput
                      inputProps={{ prefix: '$ ' }}
                      isFieldReadOnly={fields.veh_msrp_dpr.is_readonly}
                    />
                  )}
                </Stack>

                {showVehicleStatedAmount && (
                  <Stack gap={1.5}>
                    <FieldQuestionText field={fields.veh_stated_amt} />
                    <NumberFormatWithWrapper
                      field={fields.veh_stated_amt}
                      value={vehicle?.veh_stated_amt}
                      error={fieldHasError(fields.veh_stated_amt.code)}
                      helperText={fieldErrorMessage(fields.veh_stated_amt.code)}
                      currencyInput
                      inputProps={{ prefix: '$ ' }}
                      onChange={(event) => {
                        setState('veh_stated_amt', event.target.value as string);
                      }}
                    />
                  </Stack>
                )}

                <Stack gap={1.5}>
                  <QuestionEngine
                    {...questionEngineProps}
                    formik={vehicleUWFormik as QuestionEngineProps['formik']}
                    state={{
                      ...vehicle,
                      ...vehicle?.underwriting_question,
                      ...vehicleUWState,
                      ...submission?.data?.policy_data,
                      productVersion: formatThreeConfigVersion(submission?.three_config_version),
                    }}
                  />
                </Stack>
              </Stack>
            )
          }
          modalFooter={<PrimaryButton text={title} fullWidth onClick={handleAction} />}
        />
        <MissingVehicleVinModal
          open={isMissingVinModalOpen}
          onAccept={handleMissingVinAccept}
          onCancel={() => setIsMissingVinModalOpen(false)}
        />
      </>
    );
  },
);

export default VehicleModal;
